/**
 * High-level id resolver. Picks the persistent or ephemeral path based on
 * consent state, plus a per-page-load `pageviewId`.
 */

import { random64BitHex } from './random';
import { resolvePersistentUser, generateEphemeralUserId, clearPersistentUserId } from './userId';
import type { UserLifecycle } from './userId';
import {
  ensurePersistentSessionIdWithLifecycle,
  generateEphemeralSessionId,
  clearPersistentSessionId,
  readPersistentSessionId,
  writePersistentSessionId,
} from './sessionId';
import type { SessionLifecycle } from './sessionId';

export type { UserLifecycle, SessionLifecycle };

export interface IdsContext {
  rootDomain: string;
}

export interface ResolvedIds {
  /** `uid=` query value. */
  userId: string;
  /** `sid=` query value. */
  sessionId: string;
  /** `pvi=` query value — fresh on every page load. */
  pageviewId: string;
  /** `uidf=` — user visit frequency (Yoto parity). Omitted for ephemeral ids. */
  uidf?: number;
}

export interface IdentityResolution {
  ids: ResolvedIds;
  userLifecycle: UserLifecycle;
  sessionLifecycle: SessionLifecycle;
}

let pageviewIdCache: string | null = null;

export function pageviewId(): string {
  if (!pageviewIdCache) pageviewIdCache = random64BitHex();
  return pageviewIdCache;
}

/** Test-only — new pageview id on next resolve. */
export function _resetPageviewIdForTests(): void {
  pageviewIdCache = null;
}

export function resolveIdentity(ctx: IdsContext, hasConsent: boolean): IdentityResolution {
  const pvi = pageviewId();
  if (!hasConsent) {
    return {
      ids: {
        userId: generateEphemeralUserId(),
        sessionId: generateEphemeralSessionId(),
        pageviewId: pvi,
      },
      userLifecycle: null,
      sessionLifecycle: 'init',
    };
  }

  const u = resolvePersistentUser(ctx);
  const s = ensurePersistentSessionIdWithLifecycle();
  return {
    ids: {
      userId: u.userId,
      sessionId: s.sessionId,
      pageviewId: pvi,
      uidf: u.freq,
    },
    userLifecycle: u.userLifecycle,
    sessionLifecycle: s.sessionLifecycle,
  };
}

export function resolveIds(ctx: IdsContext, hasConsent: boolean): ResolvedIds {
  return resolveIdentity(ctx, hasConsent).ids;
}

export function clearPersistentIds(ctx: IdsContext): void {
  clearPersistentUserId(ctx);
  clearPersistentSessionId();
}

/** Test-only — session keys used by the SDK (isolates vitest cases). */
export function _clearSdkSessionStorageForTests(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem('kz_sid');
    sessionStorage.removeItem('kz_usess');
    sessionStorage.removeItem('kz_clickout_sess_clicks');
    sessionStorage.removeItem('kz_ecom_sess_unique_fired');
    sessionStorage.removeItem('kz_cpc_sent');
    sessionStorage.removeItem('kz_session_cpm');
    sessionStorage.removeItem('kz_last_session_value');
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key?.startsWith('kz_ecmst_') || key?.startsWith('kz_ecmata_')) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    /* ignore */
  }
}

export { random64BitHex, readPersistentSessionId, writePersistentSessionId };
